/**
 * MetricsEngagementPage — 数据中台 · 内容消费 L2-B（ADM-MTR-S003，1:1 移植原型 metrics-engagement.jsx）
 *
 * 周 cohort 留存曲线 + 人均阅读深度趋势 + 内容类型对比。数据走 useMetricsEngagement
 * （GET /api/v1/metrics/engagement，admin 自有 API，随 P1-4 dev-docs 收录）。
 * 混合就绪度（禁假数据，原型「整屏 PENDING」按服务端 as-built 收窄）：
 * - read_trend live（reading_sessions 真实源）→ LineChartX 双线（人均章节 / 人均分钟）；
 * - retention_curves 留存值 PENDING（骨架 cohort_size live）→ 整块 PendingBlock，
 *   窗内 cohort 周数 / 规模合计在说明中带出；behavior 点亮后自动切多 cohort 曲线；
 * - content_types PARTIAL：阅读用户 / 解锁转化 live，完读率 / 人均章节口径待定 → 按格灰显。
 * 空态（无阅读会话且无 cohort 行）≠ PENDING。导出依赖行为列就绪且后端无端点 → 按原型保持禁用态（非假接线）。
 */

import type { MetricsRange } from '@novel-admin/shared';
import { useState } from 'react';
import { useAppScope } from '~/app/app-scope-store';
import { useApps, useMetricsEngagement } from '~/data/queries';
import { Icons } from '~/lib/icons';
import { ChartSkeleton } from '~/ui/Skeleton';
import { Table } from '~/ui/Table';
import { LineChartX, type LineChartXSeries } from '~/ui/charts';
import {
  Cal,
  CardHead,
  LiveTag,
  MetricsToolbar,
  PendTag,
  PendingBlock,
  StateGate,
  type StateGateMode,
} from './metrics-shared';

/** cohort 曲线系列配色：按 cohort 序位循环取 chart 档位（对齐获客页渠道配色惯例） */
const COHORT_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
] as const;

/**
 * 待接入事件清单（照原型 MX_DATA.ENGAGEMENT.events 的说明性配置，非指标数据）。
 * as-built 差异：reading_sessions 为 C 端真实表已接入（驱动本页阅读深度趋势 live），
 * 其余事件仍待自建埋点 sink——状态列按此如实标注，非假数据。
 */
const INSTRUMENTATION_EVENTS: Array<{ ev: string; drives: string; note: string; live: boolean }> = [
  {
    ev: 'app_open',
    drives: '留存曲线 · DAU · D7 留存预警',
    note: '回访判定 · 周 cohort 聚合',
    live: false,
  },
  {
    ev: 'chapter_view',
    drives: '阅读深度 · 内容类型对比 · 漏斗「开始阅读」',
    note: '含 book_id / chapter_no',
    live: false,
  },
  {
    ev: 'reading_sessions',
    drives: '人均阅读时长 / 章节（本页趋势已 live）',
    note: 'C 端阅读会话表 · 会话切割 30min 无操作',
    live: true,
  },
  {
    ev: 'screen_view(store)',
    drives: '漏斗「进入充值页」',
    note: '商业化漏斗行为级次',
    live: false,
  },
];

/** 分类卡指标行：live 值正常显示，pending / 无法派生 → 灰 —（title 说明原因） */
function CatMetricRow({
  label,
  text,
  faint,
  title,
}: {
  label: string;
  text: string;
  faint: boolean;
  title?: string;
}) {
  return (
    <div title={title} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5 }}>
      <span style={{ color: 'var(--fg-faint)' }}>{label}</span>
      <span className="mono" style={{ color: faint ? 'var(--fg-faint)' : undefined }}>
        {text}
      </span>
    </div>
  );
}

export default function MetricsEngagementPage() {
  const appId = useAppScope((s) => s.appId);
  const [range, setRange] = useState<MetricsRange>('30d');

  const { data, isLoading, isError, hasData, refetch } = useMetricsEngagement({
    range,
    appScope: appId,
  });
  const { data: apps } = useApps();
  const currentAppName = appId === 'all' ? undefined : apps.find((a) => a.id === appId)?.name;

  // 四态派生（真实 query 状态，非原型演示 mode）：初次加载 → loading；失败且无旧数据 → error；
  // 成功但无阅读会话、无安装 cohort 行**且无内容类型行**才 empty（真实空态 ≠ PENDING）——
  // reading_sessions 未流入而 reading_progress/unlock_records 有数据时，内容类型对比是本页
  // 仍有值的 live 块，不得被整页空态吞掉；否则 normal
  const isEmpty =
    hasData &&
    !isError &&
    data.readTrend.points.length === 0 &&
    data.retention.cohorts.length === 0 &&
    data.contentTypes.rows.length === 0;
  const mode: StateGateMode = isLoading
    ? 'loading'
    : isError && !hasData
      ? 'error'
      : isEmpty
        ? 'empty'
        : 'normal';

  // 「数据更新至」= 数据覆盖窗上界 window.to；computed_at 是跑批时刻（凌晨跑批会虚报一天），仅作回退
  const freshness = data.window.to
    ? `数据更新至 ${data.window.to} · T+1`
    : data.computedAt
      ? `数据更新至 ${data.computedAt.slice(0, 10)} · T+1`
      : undefined;

  // 留存曲线 READY 分支（behavior 点亮后自动出图）：X 轴 = 里程碑 D1…D30，逐 cohort 一条曲线
  const retentionXLabels = data.retention.milestones.map((m) => `D${m}`);
  const retentionSeries: LineChartXSeries[] = data.retention.cohorts.map((c, i) => ({
    label: `${c.cohortWeek}（${c.cohortSize.toLocaleString()}）`,
    color: COHORT_COLORS[i % COHORT_COLORS.length] as string,
    values: c.retentionPct,
  }));

  // 留存曲线 Y 轴上界数据驱动：留存可高至 100%，硬顶 50 会把高留存 cohort（如 D1）画出界。
  // 取所有 cohort 里程碑值的最大值，向上取整到 10 的倍数、封顶 100；无数据回退 50。
  const retentionMax = Math.max(
    0,
    ...retentionSeries.flatMap((s) => s.values.filter((v): v is number => typeof v === 'number')),
  );
  const retentionYMax = retentionMax > 0 ? Math.min(100, Math.ceil(retentionMax / 10) * 10) : 50;

  // PendingBlock 说明带出 live 骨架规模（窗内周 cohort 数 + 规模合计），无行则回落原型文案
  const retentionPendingNote = `cohort_size 已就绪（user_attributions${
    data.retention.cohorts.length > 0
      ? ` · 窗内周 cohort ${data.retention.cohorts.length} 个 · 规模合计 ${data.retention.totalCohortSize.toLocaleString()}`
      : ''
  }）· 回访分子待 app_open 事件 · 接入后从当日起累积，D7 首条完整曲线约 T+8 可见`;

  const catPartial = data.contentTypes.partial || data.contentTypes.pending;

  // 阅读用户占比条基准：窗内各分类 readers 峰值（live 派生，admin-first）；空/全 0 兜底 1 防除零
  const maxReaders = Math.max(1, ...data.contentTypes.rows.map((r) => r.readers));

  return (
    <div className="page" data-screen-label="内容消费 L2-B">
      <div className="page-header">
        <div>
          <h1 className="page-title">
            内容消费{' '}
            <span style={{ color: 'var(--fg-faint)', fontWeight: 400, fontSize: 15 }}>L2-B</span>
          </h1>
          <div className="page-sub hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
            留存 × 阅读深度 × 内容类型
            <Cal t="T+1" />
            <LiveTag
              label="live·阅读会话"
              title="真实数据源：reading_sessions（C 端阅读会话行）· 人均阅读深度趋势 live"
            />
            {data.retention.pending && <PendTag k="beh" label="留存 PENDING·待埋点" />}
          </div>
        </div>
        {/* 原型同款禁用导出：留存/内容类型行为列未就绪且后端无导出端点，保持禁用态而非假接线 */}
        <button
          type="button"
          className="btn"
          disabled
          title="埋点接入后可导出"
          style={{ opacity: 0.55, cursor: 'not-allowed' }}
        >
          <Icons.IconDownload size={13} />
          导出
        </button>
      </div>

      <MetricsToolbar
        appName={currentAppName}
        range={range}
        setRange={setRange}
        freshness={freshness}
      />

      <StateGate
        mode={mode}
        skeleton={
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1fr)',
                gap: 12,
              }}
            >
              <ChartSkeleton height={240} />
              <ChartSkeleton height={240} />
            </div>
            <ChartSkeleton height={200} />
          </>
        }
        emptyTitle="当前范围暂无行为数据"
        emptyNote="该 App / 时间窗内无阅读会话 / 安装 cohort 记录"
        errorNote="GET /api/v1/metrics/engagement 加载失败 · 请重试"
        onRetry={refetch}
      >
        <>
          {/* 待接入说明条（照原型：说明而非空白）：留存等行为指标待埋点，reading_sessions 已 live */}
          {data.retention.pending && (
            <div className="card" style={{ padding: 0, overflow: 'hidden', borderStyle: 'dashed' }}>
              <div
                className="hstack"
                style={{
                  gap: 10,
                  padding: '10px 14px',
                  background: 'var(--bg-subtle)',
                  borderBottom: '1px solid var(--border)',
                  flexWrap: 'wrap',
                }}
              >
                <Icons.IconLayers size={14} style={{ color: 'var(--fg-muted)', flexShrink: 0 }} />
                <span style={{ fontSize: 12.5, fontWeight: 500 }}>留存等行为指标待埋点接入</span>
                <span className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)' }}>
                  PENDING_INSTRUMENTATION
                </span>
                <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                  自建埋点 sink（不引第三方 SDK）· 事件落库当日起 T+1 出数，无历史回补
                </span>
              </div>
              <Table className="tbl" style={{ tableLayout: 'auto' }}>
                <thead>
                  <tr>
                    <th style={{ width: 180 }}>事件</th>
                    <th>驱动指标</th>
                    <th>备注</th>
                    <th style={{ width: 90 }}>状态</th>
                  </tr>
                </thead>
                <tbody>
                  {INSTRUMENTATION_EVENTS.map((e) => (
                    <tr key={e.ev}>
                      <td className="mono" style={{ fontSize: 11.5 }}>
                        {e.ev}
                      </td>
                      <td style={{ fontSize: 12 }}>{e.drives}</td>
                      <td style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{e.note}</td>
                      <td>
                        {e.live ? (
                          <span className="chip chip-pos" style={{ fontSize: 10 }}>
                            已接入
                          </span>
                        ) : (
                          <span
                            className="chip"
                            style={{
                              fontSize: 10,
                              color: 'var(--fg-muted)',
                              borderStyle: 'dashed',
                            }}
                          >
                            待接入
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1fr)',
              gap: 12,
            }}
          >
            {/* 留存曲线（周 cohort）：留存值 PENDING → 整块 PendingBlock（骨架规模带出）；就绪后多 cohort 曲线 */}
            <div className="card" style={{ padding: 14, minWidth: 0 }}>
              <CardHead
                title="留存曲线（周 cohort）"
                sub="app_open 回访 ÷ cohort_size · D1–D30 · 越新的 cohort 曲线越短（未到期）"
                tags={['埋点·app_open', 'T+1']}
                action={
                  data.retention.pending ? <PendTag k="beh" /> : <LiveTag label="live·埋点" />
                }
              />
              {data.retention.pending ? (
                <PendingBlock
                  height={218}
                  schematic
                  title="留存曲线待 app_open 接入"
                  note={retentionPendingNote}
                />
              ) : data.retention.cohorts.length > 0 ? (
                <LineChartX
                  xLabels={retentionXLabels}
                  series={retentionSeries}
                  height={218}
                  yMax={retentionYMax}
                  yFormat={(v) => `${v.toFixed(0)}%`}
                />
              ) : (
                <div
                  style={{
                    height: 232,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11.5,
                    color: 'var(--fg-faint)',
                  }}
                >
                  当前范围无安装 cohort 记录 · 暂无留存曲线
                </div>
              )}
            </div>

            {/* 人均阅读深度：reading_sessions 真实源 live（原型此块 PENDING，as-built 已就绪出图） */}
            <div className="card" style={{ padding: 14, minWidth: 0 }}>
              <CardHead
                title="人均阅读深度"
                sub="人均章节数 / 人均时长（分钟）· 逐业务日"
                tags={['reading_sessions', 'T+1']}
                action={
                  <LiveTag
                    label="live·阅读会话"
                    title="真实数据源：reading_sessions（C 端阅读会话行）· 空数据为真实空态而非 PENDING"
                  />
                }
              />
              {data.readTrend.points.length > 0 ? (
                <LineChartX
                  xLabels={data.readTrend.points.map((p) => p.date.slice(5))}
                  series={[
                    {
                      label: '人均时长 (min)',
                      color: 'var(--chart-2)',
                      values: data.readTrend.points.map((p) => p.avgMinutes),
                    },
                    {
                      label: '人均章节数',
                      color: 'var(--chart-3)',
                      values: data.readTrend.points.map((p) => p.avgChapters),
                    },
                  ]}
                  height={218}
                  yFormat={(v) => v.toFixed(1)}
                />
              ) : (
                <div
                  style={{
                    height: 232,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11.5,
                    color: 'var(--fg-faint)',
                  }}
                >
                  当前范围无阅读会话记录 · 暂无阅读深度趋势
                </div>
              )}
            </div>
          </div>

          {/* 内容类型对比：分类闭集（配置）× 阅读用户/解锁转化 live · 完读率/人均章节口径待定按格灰显 */}
          <div className="card" style={{ padding: 14 }}>
            <CardHead
              title="内容类型表现对比"
              sub="分类 × 阅读用户 × 完读率 × 解锁转化 × 人均章节 · 分类沿用书籍管理闭集（配置已就绪）"
              tags={['埋点·chapter_view', 'T+1']}
              action={catPartial ? <PendTag k="beh" label="部分列 PENDING" /> : undefined}
            />
            {data.contentTypes.rows.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10 }}>
                {data.contentTypes.rows.map((cat) => (
                  <div
                    key={cat.categoryId}
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      padding: 12,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      minWidth: 0,
                    }}
                  >
                    <div className="hstack" style={{ justifyContent: 'space-between' }}>
                      <span className="chip" style={{ color: 'var(--fg-muted)' }}>
                        {cat.name}
                      </span>
                      <span
                        className="mono"
                        title="阅读用户（窗内 reading_progress · live）"
                        style={{ fontSize: 10.5 }}
                      >
                        {cat.readers.toLocaleString()}
                      </span>
                    </div>
                    {/* 阅读用户占比条：填充宽度 = 该分类 readers ÷ 窗内峰值（live 派生，1:1 还原设计） */}
                    <div
                      style={{
                        height: 4,
                        borderRadius: 2,
                        background: 'var(--bg-muted)',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${(cat.readers / maxReaders) * 100}%`,
                          height: '100%',
                          background: 'var(--accent)',
                          borderRadius: 2,
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <CatMetricRow
                        label="完读率"
                        text={cat.finishRate == null ? '—' : `${cat.finishRate.toFixed(1)}%`}
                        faint={cat.finishRate == null}
                        title="完读率口径待定（完读判定阈值）→ 暂不出数（禁臆造）"
                      />
                      <CatMetricRow
                        label="解锁转化"
                        text={cat.unlockRate == null ? '—' : `${cat.unlockRate.toFixed(1)}%`}
                        faint={cat.unlockRate == null}
                        title="解锁付费章节用户 ÷ 分类阅读用户（同窗）· live；无阅读用户 → —。分子分母各自独立折叠已注销用户为 1，偏移方向不固定 · 观测近似，非结算"
                      />
                      <CatMetricRow
                        label="人均章节"
                        text={cat.avgChapters == null ? '—' : cat.avgChapters.toFixed(1)}
                        faint={cat.avgChapters == null}
                        title="人均章节口径待定（per-category）→ 暂不出数（禁臆造）"
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                style={{
                  padding: 18,
                  textAlign: 'center',
                  fontSize: 11.5,
                  color: 'var(--fg-faint)',
                }}
              >
                分类闭集为空 · 请先在书籍管理维护启用分类
              </div>
            )}
            <div style={{ marginTop: 10, fontSize: 10.5, color: 'var(--fg-faint)' }}>
              阅读用户 / 解锁转化 live（reading_progress / unlock_records 同窗口径）· 完读率 /
              人均章节口径待定 → 暂不出数（就绪后按分类自动点亮）
            </div>
            <div style={{ marginTop: 4, fontSize: 10.5, color: 'var(--fg-muted)' }}>
              已注销用户经匿名化折叠为 1 · 解锁转化分子分母各自折叠、偏移方向不固定 ·
              观测近似，非结算
            </div>
          </div>
        </>
      </StateGate>
    </div>
  );
}
